import { TabList, Tabs, TabsProps } from '@homebotapp/hb-react-component-catalog'
import { selectClientShouldSeeMarketsAndBuyerNavButtons } from '../../../store/selectors/navigation'
import { useSelector } from 'react-redux'
import { OwnTab } from './OwnTab'
import { MarketsTab } from './MarketsTab'
import { SearchTab } from './SearchTab'
import { BuyTab } from './BuyTab'
import { selectClientIsAProspect } from '../../../store/selectors/client'
import { selectHomebotListingsFeatureEnabled, selectIsSoloRea } from '../../../store/selectors/customerProfile'
import { selectActiveNavigationTab } from '../../../store/slices/navigation'
import { NAVIGATION_TAB_NAMES, NavigationTabProps } from '../../../types/navigation'
import { ForYouTab } from './ForYouTab'
import { CalculatorsTab } from './CalculatorsTab'
import { ProfileTab } from './ProfileTab'

type NavigationTabType = typeof OwnTab | typeof BuyTab | typeof MarketsTab | typeof SearchTab | typeof ProfileTab

type NavigationTabsProps = Omit<TabsProps, 'children'> & {
  children?: React.ReactNode
}

// If this is too constricting of an abstraction, we can make this
// component just take children and pass a list of tabs
// in the DesktopHeader and BottomNav components
export const NavigationTabs = (tabsProps: NavigationTabsProps) => {
  const activeNavigationTab = useSelector(selectActiveNavigationTab)
  const showMarketsAndBuyerNavButtons = useSelector(selectClientShouldSeeMarketsAndBuyerNavButtons)
  const searchEnabled = useSelector(selectHomebotListingsFeatureEnabled)
  const isSoloREA = useSelector(selectIsSoloRea)
  const isAProspect = useSelector(selectClientIsAProspect)

  // Naive lookup table to map tab names to components
  const tabNameToComponentMap = {
    [NAVIGATION_TAB_NAMES.HOMES]: (props: NavigationTabProps) => <OwnTab {...props} />,
    [NAVIGATION_TAB_NAMES.CALCULATORS]: (props: NavigationTabProps) => <CalculatorsTab {...props} />,
    [NAVIGATION_TAB_NAMES.FORYOU]: (props: NavigationTabProps) =>
      // if client has only listings enabled, they can see galleries
      // if client has ability to become buyer, they can onboard and then see markets pill link in for-you
      // if neither of these is true, hide the for-you tab because the whole thing would be blank/inaccessible
      (searchEnabled || showMarketsAndBuyerNavButtons) && <ForYouTab {...props} />,
    [NAVIGATION_TAB_NAMES.SEARCH]: (props: NavigationTabProps) => searchEnabled && <SearchTab {...props} />,
    [NAVIGATION_TAB_NAMES.PROFILE]: (props: NavigationTabProps) => <ProfileTab {...props} />
  }

  if (isSoloREA) {
    delete tabNameToComponentMap[NAVIGATION_TAB_NAMES.CALCULATORS]
  }

  if (isAProspect) {
    delete tabNameToComponentMap[NAVIGATION_TAB_NAMES.HOMES]
  }

  // remove entries from tabNameToComponentMap if the value is falsey
  const filteredTabNameToComponentMap = Object.fromEntries(
    Object.entries(tabNameToComponentMap).filter(([key, value]) => value)
  )

  const indexOfActiveNavigationTab =
    activeNavigationTab === NAVIGATION_TAB_NAMES.MARKETS
      ? Object.keys(filteredTabNameToComponentMap).indexOf(NAVIGATION_TAB_NAMES.FORYOU)
      : Object.keys(filteredTabNameToComponentMap).indexOf(activeNavigationTab)

  return (
    <Tabs {...tabsProps} index={indexOfActiveNavigationTab} isLazy height='100%'>
      <TabList overflowX='auto' overflowY='hidden' height='100%'>
        {Object.values(filteredTabNameToComponentMap).map((Component: NavigationTabType, index) => (
          <Component key={index} isSelected={indexOfActiveNavigationTab === index} />
        ))}
      </TabList>
    </Tabs>
  )
}
